/**
 * Fairness gates (03 §5.2) and the limits of 02 §9 and §12: most of the drop band is free
 * of enemies, no emplacement covers it or the pickup, the specials respect the threat
 * rating, and the map stays within its prop and garrison budgets.
 */

import { angleDiff, atan2, PI } from '../../sim/math';
import { ENEMY } from '../../sim/tuning';
import type { MissionDef } from '../../sim/types';
import { LIMITS, VALIDATE } from '../tuning';
import { propLoad } from '../propLoad';

export function checkFairness(def: MissionDef): string[] {
  const out: string[] = [];
  const present = def.spawns.filter((s) => s.dormantUntil === undefined);

  let points = 0;
  let free = 0;
  const r2 = VALIDATE.enemyFreeRadius * VALIDATE.enemyFreeRadius;
  for (let z = def.dropBand.z0 + 2; z < def.dropBand.z1; z += 4) {
    for (let x = 2; x < def.sizeX; x += 4) {
      points++;
      if (!present.some((s) => (s.x - x) * (s.x - x) + (s.z - z) * (s.z - z) < r2)) free++;
    }
  }
  if (points > 0 && free / points < VALIDATE.enemyFreeFraction) {
    const share = Math.round((free / points) * 100);
    out.push(`only ${share} % of the drop band is more than ${VALIDATE.enemyFreeRadius} m from an enemy`);
  }
  const near = (s: { x: number; z: number }): boolean =>
    (s.x - def.player.x) * (s.x - def.player.x) + (s.z - def.player.z) * (s.z - def.player.z) < r2;
  // A prisoner starts among his guards (10 §2, mission 32): what must hold there is `checkCaptured`'s business.
  if (def.startCaptured !== true && present.some(near)) {
    out.push(`an enemy stands within ${VALIDATE.enemyFreeRadius} m of the ranger's start`);
  }

  const arc = (ENEMY.emplacementArcDeg * PI) / 180;
  for (const s of present) {
    if (s.mans === undefined) continue;
    if (s.z > def.dropBand.z0 - ENEMY.emplacementRange) {
      out.push(`the emplacement at ${s.x}, ${s.z} covers the drop band`);
    }
    const dx = def.pickup.x - s.x;
    const dz = def.pickup.z - s.z;
    const inRange = dx * dx + dz * dz < ENEMY.emplacementRange * ENEMY.emplacementRange;
    if (inRange && Math.abs(angleDiff(s.facing, atan2(dx, dz))) < arc) {
      out.push(`the emplacement at ${s.x}, ${s.z} covers the pickup`);
    }
  }

  const special = (kind: string): boolean => kind === 'flamethrower' || kind === 'rocketTrooper';
  if (def.threat < LIMITS.specialsFrom && present.some((s) => special(s.kind))) {
    out.push(`flamethrowers and rocket troopers only appear from threat ${LIMITS.specialsFrom}`);
  }
  // The SAM site's and the coastal battery's own mini-bunkers are the mission (03 §2 mission 7, 10 §2 mission 19).
  const own = def.archetype === 'sam-site' || def.archetype === 'coastal-guns';
  const early = !own && def.threat < LIMITS.miniBunkerFrom;
  if (early && present.some((s) => s.kind === 'miniBunker')) {
    out.push(`mini-bunkers outside the SAM site only appear from threat ${LIMITS.miniBunkerFrom}`);
  }
  if (def.spawns.length < LIMITS.spawnsMin || def.spawns.length > LIMITS.spawnsAt9) {
    out.push(`${def.spawns.length} spawns: the garrison must number ${LIMITS.spawnsMin}-${LIMITS.spawnsAt9}`);
  }
  // Counted as scatter counts it: a wall by its length (propLoad.ts, ROUND7).
  const load = propLoad(def.props);
  if (load > LIMITS.props + 10) {
    out.push(`${Math.round(load)} props: the map must stay under ${LIMITS.props + 10}`);
  }
  return out;
}
